import Badge from '@pelckmans/elementis/components/badge';
import Button from '@pelckmans/elementis/components/button';
import Section from '@pelckmans/elementis/components/section';
import FilterOffIcon from '@pelckmans/elementis/icons/filter-off';
import FilterOnIcon from '@pelckmans/elementis/icons/filter-on';
import { RefObject, useContext, useId, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalContext from '../../../../../contexts/ModalContext';
import useDocumentTitle from '../../../../../hooks/useDocumentTitle';
import useLicenses from '../../../../../hooks/useLicenses';
import LoadingState from '../../../../../shared-components/LoadingState';
import HorizontalScrollContext, {
  HorizontalScrollContextProvider,
} from '../../../../../shared-components/table/context/HorizontalScrollContext';
import { useResultsDataContext } from '../../context/ResultsDataContext';
import { useFilters } from '../../context/filter-context/FilterContext';
import { renderStudentFullName } from '../../utils/render-utils';
import ResultsEmptyState from '../ResultsEmptyState';
import { useScorableItemTOCHierarchy } from '../exercise/hooks/useScorableItemTOCHierarchy';
import ExportButton from '../export-button/ExportButton';
import ResultsTable from '../table/ResultsTable';
import StudentFilter from './StudentFilter';
import ItemFilter from './components/ItemFilter';
import FilterStudentsModal from './components/filter-students-modal/FilterStudentsModal';
import { useTableData } from './hooks/useTableData';
import { getStylePropertyValue } from '../../../../../utils/style';

function StudentTab({
  containerRef,
  tableRef,
}: {
  containerRef: RefObject<HTMLDivElement>;
  tableRef: RefObject<HTMLTableElement>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const id = useId();

  useDocumentTitle(t('PAGES.RESULTS.PER_STUDENT.DOCUMENT_TITLE'));

  const { isModuleLicensed } = useLicenses();

  const { showModal } = useContext(ModalContext);
  const { scorableItems, scorableItemsPerSchoolYearPerModule, members, groups } = useResultsDataContext();
  const isDeeplink = location.key === 'default';

  const {
    setSelectedMembers,
    selectedSchoolYear,
    selectedModule,
    selectedGroups,
    clearFilters,
    determiningFilters,
    usedLocalStorageFilters,
    spMemberIdsPerGroup,
  } = useFilters();

  const isDetailView = selectedGroups.length === 1 && spMemberIdsPerGroup?.[selectedGroups[0].id]?.length === 1;

  const isUnlicensed = !isModuleLicensed(selectedModule);

  const { horizontalScroll } = useContext(HorizontalScrollContext);

  const { data, usedMembers, dataIsLoading, hasResults, results, exportData, fetchData } = useTableData({
    showDetailView: isDetailView,
    horizontalScroll,
  });

  const { tocAndScorableItems } = useScorableItemTOCHierarchy();

  const title =
    !usedMembers.length || !isDetailView
      ? t('PAGES.RESULTS.PER_STUDENT.SECTION_TITLE')
      : t('PAGES.RESULTS.PER_STUDENT.SECTION_TITLE_SINGLE_STUDENT', {
          studentName: renderStudentFullName(usedMembers[0]),
        });

  const backProps = useMemo(() => {
    return isDetailView
      ? {
          onBackClick: () => {
            if (usedLocalStorageFilters) {
              setSelectedMembers([]);
            } else {
              navigate(-1);
            }
          },
          backButtonLabel: t('PAGES.RESULTS.PER_STUDENT.BACK_BUTTON_A11Y_LABEL'),
        }
      : {};
  }, [usedLocalStorageFilters, navigate, setSelectedMembers, isDetailView, t]);

  const filtersDisabled =
    isUnlicensed ||
    (scorableItemsPerSchoolYearPerModule[selectedSchoolYear?.id || '']?.[selectedModule?.id || ''] || []).length === 0;

  const noGroups = groups.filter(x => x.acceptedMemberCount > 0).length === 0;
  const noGroupsForFilter = !selectedGroups.length;

  const filterProps = useMemo(() => {
    const membersFilterActive =
      usedMembers.length > 0 && usedMembers.length !== members[selectedGroups[0!].id || '']?.length;

    const clearStudentsFilterLabel = t('PAGES.RESULTS.PER_STUDENT.FILTER.STUDENTS.CLEAR');

    return !noGroups && !isDetailView
      ? {
          filter: <StudentFilter filtersDisabled={filtersDisabled} />,
          filterActions: (
            <>
              <Button
                startIcon={<FilterOnIcon />}
                size="sm"
                prominence="neutral"
                disabled={filtersDisabled}
                onClick={() => {
                  showModal(
                    <FilterStudentsModal
                      id="filter-students-modal"
                      allMembers={members[selectedGroups[0!].id || ''] || []}
                      selectedMembers={usedMembers}
                      setSelectedMembers={members => {
                        setSelectedMembers(members.map(m => `${selectedGroups[0!].id};${m.id}`));
                      }}
                    />,
                  );
                }}
                badge={
                  membersFilterActive ? (
                    <Badge prominence="ghost" size="sm">
                      {usedMembers.length}
                    </Badge>
                  ) : undefined
                }
                key="open-filter-students-modal"
              >
                {t('PAGES.RESULTS.PER_STUDENT.FILTER.STUDENTS.OPEN_MODAL_BUTTON')}
              </Button>
              {membersFilterActive && (
                <Button
                  startIcon={<FilterOffIcon data-testid="filter-off-icon" />}
                  aria-label={clearStudentsFilterLabel}
                  tooltip={clearStudentsFilterLabel}
                  onClick={() => setSelectedMembers([])}
                  key="clear-students-filter"
                  prominence="neutral"
                />
              )}
            </>
          ),
          filterActionsGroupRole: 'group',
          filterActionsAriaLabel: t('PAGES.RESULTS.PER_STUDENT.FILTER.STUDENTS.BUTTON_GROUP_A11Y_LABEL'),
          filterMobileButtonTitle: t('PAGES.RESULTS.PER_STUDENT.FILTER.STUDENTS.MOBILE_BUTTON.TITLE'),
          filterMobileButtonActiveTitle: t('PAGES.RESULTS.PER_STUDENT.FILTER.STUDENTS.MOBILE_BUTTON.ACTIVE_TITLE'),
        }
      : {};
  }, [filtersDisabled, noGroups, members, usedMembers, selectedGroups, setSelectedMembers, isDetailView, showModal, t]);

  const actionProps = useMemo(() => {
    return !noGroups
      ? {
          actions: hasResults && !dataIsLoading && (
            <ExportButton results={results} xlsxData={exportData} fetchData={fetchData} />
          ),
          actionGroupRole: undefined,
          actionGroupAriaLabel: undefined,
        }
      : {};
  }, [dataIsLoading, hasResults, noGroups, results, exportData, fetchData]);

  const noScorableItems = scorableItems.length === 0;
  const noScorableItemsForFilter = useMemo(() => {
    if (!selectedSchoolYear || !selectedModule || !hasResults) return true;

    const items = scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id]?.[selectedModule.id] || [];

    return items.length === 0;
  }, [scorableItemsPerSchoolYearPerModule, selectedModule, selectedSchoolYear, hasResults]);

  const renderTable = () => {
    if (dataIsLoading || !selectedModule || determiningFilters) {
      return <LoadingState variant="table" aria-label={t('PAGES.RESULTS.PER_STUDENT.LOADING_STATE.A11Y_LABEL')} />;
    }
    if (isUnlicensed) return <ResultsEmptyState variant="no-license" id={id} />;

    if (noGroups) return <ResultsEmptyState variant="no-groups" id={id} />;
    if (noGroupsForFilter) return <ResultsEmptyState variant="no-groups-filter" id={id} />;
    if (noScorableItems)
      return <ResultsEmptyState variant="no-scorable-items" moduleNiceName={selectedModule.niceName} id={id} />;
    if (noScorableItemsForFilter)
      return (
        <ResultsEmptyState
          variant="no-scorable-items-filter"
          onClear={() => {
            clearFilters();
          }}
          id={id}
        />
      );

    return (
      <ResultsTable
        data={data}
        detailView={isDetailView}
        ref={tableRef}
        cellHeaderActions={<ItemFilter tableDataGroups={tocAndScorableItems} />}
        id={id}
      />
    );
  };

  const firstColumnMaxWidthInPx = getStylePropertyValue('--table-column-size-xl-width-max');
  const firstColumnMaxWidth = Number(firstColumnMaxWidthInPx.replace('px', ''));

  useLayoutEffect(() => {
    function applyFirstColumnWidth() {
      if (!containerRef.current || !tableRef.current) return;

      const { width: containerWidth } = containerRef.current.getBoundingClientRect();
      const columnWidth = 84;

      const growFirstColumn = firstColumnMaxWidth + data.header.columns.length * columnWidth < containerWidth;

      const calculatedFirstColumnWidth = growFirstColumn
        ? containerWidth - data.header.columns.length * columnWidth
        : firstColumnMaxWidth;

      tableRef.current.style.setProperty('--scorable-item-width', calculatedFirstColumnWidth + 'px');
    }

    applyFirstColumnWidth();

    window.addEventListener('resize', applyFirstColumnWidth);

    const resizeObserver = new ResizeObserver(applyFirstColumnWidth);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', applyFirstColumnWidth);
      resizeObserver.disconnect();
    };
  }, [containerRef, data.header.columns.length, firstColumnMaxWidth, tableRef]);

  return (
    <Section
      title={title}
      {...actionProps}
      {...(!isDeeplink && { ...backProps })}
      {...filterProps}
      className="results-student-tab"
      contentClassName="content-wrapper"
      sectionLabelId={id}
    >
      <div className="scroll-table-container" ref={containerRef}>
        {renderTable()}
      </div>
    </Section>
  );
}

export default function WrappedStudentTab() {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  return (
    <HorizontalScrollContextProvider containerRef={containerRef} tableRef={tableRef}>
      <StudentTab containerRef={containerRef} tableRef={tableRef} />
    </HorizontalScrollContextProvider>
  );
}
